import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { subnetNames } from '../../utils/subnetNames';
import { formatDistanceToNow } from 'date-fns';
import { generateMarketAnalysis } from '../openaiService';

interface EmissionsEntry {
  block_number: number;
  timestamp: string;
  emissions: number;
  emissions_24h: number;
  avg_emissions_per_block: number;
}

export async function getEmissionsReport(netuid: number): Promise<string> {
  try {
    // Get the latest emissions entry
    const entriesRef = collection(db, 'emissions', netuid.toString(), 'entries');
    const q = query(
      entriesRef,
      orderBy('block_number', 'desc'),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return `No emissions data available for ${subnetNames[netuid]} (SN${netuid}).`;
    }

    const latestData = snapshot.docs[0].data() as EmissionsEntry;
    
    // Get historical data for comparison
    const oneDayAgo = new Date();
    oneDayAgo.setHours(oneDayAgo.getHours() - 24);
    
    const historyQuery = query(
      entriesRef,
      where('timestamp', '>=', oneDayAgo.toISOString()),
      orderBy('timestamp', 'asc')
    );
    
    const historySnapshot = await getDocs(historyQuery);
    const historyEntries = historySnapshot.docs.map(doc => doc.data() as EmissionsEntry);
    
    // Calculate changes if we have historical data
    let emissionsChange = 0;
    let avgEmissionsChange = 0;
    
    if (historyEntries.length >= 2) {
      const oldestEntry = historyEntries[0];
      emissionsChange = ((latestData.emissions_24h - oldestEntry.emissions_24h) / oldestEntry.emissions_24h) * 100;
      avgEmissionsChange = ((latestData.avg_emissions_per_block - oldestEntry.avg_emissions_per_block) / oldestEntry.avg_emissions_per_block) * 100;
    }

    const timeAgo = formatDistanceToNow(new Date(latestData.timestamp), { addSuffix: true });
    
    // Prepare data for AI analysis
    let dataForAnalysis = `Emissions Report for ${subnetNames[netuid]} (SN${netuid})\n`;
    dataForAnalysis += `Last Updated: ${timeAgo}\n\n`;
    
    dataForAnalysis += `Total Emissions: ${formatEmissions(latestData.emissions)} τ\n`;
    dataForAnalysis += `24h Emissions: ${formatEmissions(latestData.emissions_24h)} τ`;
    
    if (emissionsChange !== 0) {
      dataForAnalysis += ` (${emissionsChange >= 0 ? '+' : ''}${emissionsChange.toFixed(2)}% change)\n`;
    } else {
      dataForAnalysis += '\n';
    }
    
    dataForAnalysis += `Average Per Block: ${formatEmissions(latestData.avg_emissions_per_block)} τ`;
    if (avgEmissionsChange !== 0) {
      dataForAnalysis += ` (${avgEmissionsChange >= 0 ? '+' : ''}${avgEmissionsChange.toFixed(2)}% change)`;
    }
    
    dataForAnalysis += `\nBlock: ${latestData.block_number.toLocaleString()}`;

    // Add historical data if available
    if (historyEntries.length > 2) {
      dataForAnalysis += '\n\nEmissions Trend:';
      // Include a few sample points to show the trend
      const samplePoints = historyEntries.filter((_, index) => index % Math.ceil(historyEntries.length / 5) === 0);
      samplePoints.forEach(entry => {
        const entryTime = formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true });
        dataForAnalysis += `\n${entryTime}: ${formatEmissions(entry.emissions_24h)} τ (24h)`;
      });
    }

    // Generate analysis using OpenAI
    const analysis = await generateMarketAnalysis(dataForAnalysis);
    return analysis;
  } catch (error) {
    console.error('Error generating emissions report:', error);
    return 'Error: Failed to generate emissions report.';
  }
}

function formatEmissions(value: number): string {
  return (value / 1e9).toFixed(4);
}